<template>
  <header class="header">
    <div class="header-inner">
      <div class="header-left">
        <div class="brand" @click="goHome">
          <div class="brand-logo">T</div>
          <div class="brand-text">
            <div class="brand-title">Auto test</div>
            <div class="brand-subtitle">e-solution.uz</div>
          </div>
        </div>
      </div>

      <div class="header-center">
        <div class="payment-pill" :class="paymentStatusClass">
          <span class="payment-dot"></span>
          <span class="payment-label">{{ paymentLabel }}</span>
        </div>
      </div>

      <div class="header-right">
        <button class="theme-btn" @click="toggleTheme" :title="theme === 'dark' ? 'Light mode' : 'Dark mode'">
          <span class="theme-icon">{{ theme === 'dark' ? '☀️' : '🌙' }}</span>
          <span class="theme-text">{{ theme === 'dark' ? 'Light' : 'Dark' }}</span>
        </button>

        <div class="user-dropdown" @click="toggleDropdown">
          <button class="user-trigger" type="button">
            <div class="user-avatar">{{ userInitial }}</div>
            <div class="user-meta">
              <div class="user-name">{{ userName }}</div>
              <div class="user-phone">{{ userPhone }}</div>
            </div>
            <span class="user-caret" :class="{ 'open': dropdownOpen }">⌄</span>
          </button>

          <div class="dropdown-menu" v-if="dropdownOpen" @click.stop>
            <div class="menu-profile">
              <div class="menu-avatar">{{ userInitial }}</div>
              <div class="menu-meta">
                <div class="menu-name">{{ userName }}</div>
                <div class="menu-phone">{{ userPhone }}</div>
                <div class="menu-premium" v-if="userData.is_premium">
                  <span class="premium-badge">💎 Premium</span>
                  <span class="premium-until">{{ premiumUntilFormatted }}</span>
                </div>
              </div>
            </div>

            <div class="menu-divider"></div>

            <div class="menu-item" @click="goHome">
              <span class="menu-row-icon">🏠</span>
              <span>Bosh sahifa</span>
            </div>

            <div class="menu-item" @click="toggleTheme">
              <span class="menu-row-icon">{{ theme === 'dark' ? '☀️' : '🌙' }}</span>
              <span>{{ theme === 'dark' ? 'Light mode' : 'Dark mode' }}</span>
            </div>

            <div class="menu-divider"></div>

            <div class="menu-item danger" @click="handleLogout">
              <span class="menu-row-icon">⎋</span>
              <span>Chiqish</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-overlay" v-if="showLogoutConfirm" @click="cancelLogout">
      <div class="modal-container" @click.stop>
        <div class="modal-header">
          <h3>Tizimdan chiqish</h3>
        </div>
        <div class="modal-body">
          <p>Rostdan ham tizimdan chiqmoqchimisiz?</p>
        </div>
        <div class="modal-footer">
          <button class="btn-cancel" @click="cancelLogout">Bekor qilish</button>
          <button class="btn-confirm" @click="confirmLogout">Ha, chiqaman</button>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import axios from 'axios'

// API bazaviy URL
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

export default {
  data() {
    return {
      dropdownOpen: false,
      showLogoutConfirm: false,
      theme: localStorage.getItem('theme') || 'light',
      userData: {
        id: null,
        phone: '',
        first_name: '',
        last_name: '',
        full_name: '',
        is_staff: false,
        is_superuser: false,
        telegram_id: null,
        is_premium: false,
        premium_until: null,
        days_left: 0,
        balance: 0
      },
      loading: false,
      error: null
    }
  },

  computed: {
    userName() {
      return this.userData.full_name || this.userData.first_name || 'Foydalanuvchi'
    },

    userPhone() {
      return this.userData.phone || '+998'
    },

    userInitial() {
      return this.userName?.charAt(0)?.toUpperCase() || 'U'
    },

    premiumUntil() {
      return this.userData.premium_until
    },

    premiumUntilFormatted() {
      if (!this.premiumUntil) return ''
      try {
        const date = new Date(this.premiumUntil)
        return date.toLocaleDateString('uz-UZ', { day: 'numeric', month: 'short', year: 'numeric' })
      } catch {
        return ''
      }
    },

    daysLeft() {
      if (this.userData.days_left !== undefined && this.userData.days_left !== null) {
        return this.userData.days_left
      }
      if (!this.premiumUntil) return null
      try {
        const today = new Date()
        const current = new Date(today.getFullYear(), today.getMonth(), today.getDate())
        const due = new Date(this.premiumUntil)
        const dueDate = new Date(due.getFullYear(), due.getMonth(), due.getDate())
        const diff = dueDate - current
        return Math.ceil(diff / (1000 * 60 * 60 * 24))
      } catch {
        return null
      }
    },

    paymentLabel() {
      if (this.daysLeft === null) return "To'lov muddati kiritilmagan"
      if (this.daysLeft < 0) return "To'lov muddati o'tgan"
      if (this.daysLeft === 0) return 'Bugun to\'lov kuni'
      if (this.daysLeft === 1) return 'To\'lovgacha 1 kun qoldi'
      return `To'lovgacha ${this.daysLeft} kun qoldi`
    },

    paymentStatusClass() {
      if (this.daysLeft === null) return 'is-neutral'
      if (this.daysLeft < 0) return 'is-danger'
      if (this.daysLeft <= 3) return 'is-warning'
      return 'is-good'
    }
  },

  mounted() {
    // Theme ni qo'llash
    document.documentElement.setAttribute('data-theme', this.theme)
    
    // User ma'lumotlarini olish
    this.fetchUserData()
    
    // Click outside handler
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    async fetchUserData() {
      try {
        const token = localStorage.getItem('accessToken')
        if (!token) {
          console.log('No token found')
          return
        }

        console.log('Fetching user data with token:', token.substring(0, 20) + '...')
        console.log('API URL:', `${API_BASE_URL}/account/me/`)

        this.loading = true
        this.error = null
        
        const response = await axios.get(`${API_BASE_URL}/account/me/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        console.log('User data response:', response.data)

        if (response.data) {
          this.userData = {
            id: response.data.id,
            phone: response.data.phone,
            first_name: response.data.first_name,
            last_name: response.data.last_name,
            full_name: response.data.full_name,
            is_staff: response.data.is_staff,
            is_superuser: response.data.is_superuser,
            telegram_id: response.data.telegram_id,
            is_premium: response.data.is_premium || false,
            premium_until: response.data.premium_until,
            days_left: response.data.days_left || 0,
            balance: response.data.balance || 0
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error)
        if (error.response) {
          console.error('Response status:', error.response.status)
          console.error('Response data:', error.response.data)
          if (error.response.status === 401) {
            this.clearAuthAndRedirect()
          }
        } else if (error.request) {
          console.error('No response received:', error.request)
          this.error = 'Serverga ulanishda xatolik'
        } else {
          console.error('Error:', error.message)
        }
      } finally {
        this.loading = false
      }
    },

    toggleTheme() {
      this.theme = this.theme === 'dark' ? 'light' : 'dark'
      document.documentElement.setAttribute('data-theme', this.theme)
      localStorage.setItem('theme', this.theme)
    },

    toggleDropdown() {
      this.dropdownOpen = !this.dropdownOpen
    },

    handleClickOutside(event) {
      if (this.dropdownOpen && !event.target.closest('.user-dropdown')) {
        this.dropdownOpen = false
      }
    },

    goHome() {
      this.$router.push('/')
      this.dropdownOpen = false
    },

    handleLogout() {
      this.dropdownOpen = false
      this.showLogoutConfirm = true
    },

    async confirmLogout() {
      try {
        const token = localStorage.getItem('access_token')
        const refreshToken = localStorage.getItem('refresh_token')
        
        if (token && refreshToken) {
          try {
            await axios.post(`${API_BASE_URL}/account/logout/`, {
              refresh: refreshToken
            }, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            })
          } catch (err) {
            console.error('Logout API error:', err)
          }
        }
        
        this.clearAuthAndRedirect()
        
      } catch (error) {
        console.error('Logout error:', error)
        this.clearAuthAndRedirect()
      }
    },

    clearAuthAndRedirect() {
      // localStorage ni to'liq tozalash
      localStorage.clear()
      
      // sessionStorage ni tozalash
      sessionStorage.clear()
      
      // User data ni tozalash
      this.userData = {
        id: null,
        phone: '',
        first_name: '',
        last_name: '',
        full_name: '',
        is_staff: false,
        is_superuser: false,
        telegram_id: null,
        is_premium: false,
        premium_until: null,
        days_left: 0,
        balance: 0
      }
      
      // Modalni yopish
      this.showLogoutConfirm = false
      this.dropdownOpen = false
      
      // Login pagega redirect
      this.$router.push('/login')
    },

    cancelLogout() {
      this.showLogoutConfirm = false
    }
  }
}
</script>


<style scoped>
.header {
  position: sticky;
  top: 0;
  z-index: 100;
  backdrop-filter: blur(20px);
  background: rgba(var(--bg-rgb), 0.85);
  border-bottom: 1px solid var(--border);
}

.header-inner {
  max-width: 1680px;
  margin: 0 auto;
  height: 72px;
  padding: 0 24px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 18px;
}

.header-left {
  min-width: 0;
}

.header-center {
  display: flex;
  justify-content: center;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  user-select: none;
}

.brand-logo {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 18px;
  color: white;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.28);
  flex-shrink: 0;
}

.brand-text {
  min-width: 0;
}

.brand-title {
  font-size: 18px;
  line-height: 1.1;
  font-weight: 800;
  color: var(--text);
  letter-spacing: 0.2px;
}

.brand-subtitle {
  margin-top: 3px;
  font-size: 12px;
  color: var(--text-soft);
}

.payment-pill {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: 1px solid var(--border);
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(0,0,0,0.06);
}

.payment-dot {
  width: 9px;
  height: 9px;
  border-radius: 999px;
  flex-shrink: 0;
}

.payment-pill.is-good {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.08);
  border-color: rgba(34, 197, 94, 0.22);
}

.payment-pill.is-good .payment-dot {
  background: #22c55e;
  box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.14);
}

.payment-pill.is-warning {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.08);
  border-color: rgba(245, 158, 11, 0.22);
}

.payment-pill.is-warning .payment-dot {
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(245, 158, 11, 0.14);
}

.payment-pill.is-danger {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.08);
  border-color: rgba(239, 68, 68, 0.22);
}

.payment-pill.is-danger .payment-dot {
  background: #ef4444;
  box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.14);
}

.payment-pill.is-neutral {
  color: var(--text-soft);
  background: rgba(148, 163, 184, 0.08);
  border-color: rgba(148, 163, 184, 0.18);
}

.payment-pill.is-neutral .payment-dot {
  background: #94a3b8;
  box-shadow: 0 0 0 4px rgba(148, 163, 184, 0.12);
}

.theme-btn,
.user-trigger {
  border: 1px solid var(--border);
  background: rgba(255,255,255,0.04);
  color: var(--text);
  transition: all 0.2s ease;
}

:global([data-theme='light']) .theme-btn,
:global([data-theme='light']) .user-trigger {
  background: rgba(255,255,255,0.78);
}

.theme-btn {
  height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font: inherit;
}

.theme-btn:hover,
.user-trigger:hover {
  transform: translateY(-1px);
  border-color: rgba(59,130,246,0.28);
  background: rgba(59,130,246,0.08);
}

.theme-icon {
  font-size: 15px;
  line-height: 1;
}

.theme-text {
  font-size: 13px;
  font-weight: 700;
}

.user-dropdown {
  position: relative;
}

.user-trigger {
  min-height: 44px;
  padding: 5px 10px 5px 6px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font: inherit;
}

.user-avatar {
  width: 34px;
  height: 34px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  color: #fff;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  flex-shrink: 0;
}

.user-meta {
  line-height: 1.1;
  text-align: left;
}

.user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  white-space: nowrap;
}

.user-phone {
  margin-top: 3px;
  font-size: 11px;
  color: var(--text-soft);
  white-space: nowrap;
}

.user-caret {
  color: var(--text-soft);
  font-size: 14px;
  transition: transform 0.2s ease;
}

.user-caret.open {
  transform: rotate(180deg);
}

.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 280px;
  background: rgba(16, 24, 40, 0.96);
  backdrop-filter: blur(20px);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 8px;
  box-shadow: 0 18px 40px rgba(0,0,0,0.16);
  z-index: 1000;
}

:global([data-theme='light']) .dropdown-menu {
  background: rgba(255,255,255,0.98);
}

.menu-profile {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 8px;
}

.menu-avatar {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 18px;
  color: #fff;
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  flex-shrink: 0;
}

.menu-meta {
  min-width: 0;
  flex: 1;
}

.menu-name {
  font-weight: 700;
  font-size: 14px;
  color: var(--text);
}

.menu-phone {
  font-size: 12px;
  color: var(--text-soft);
  margin-top: 2px;
}

.menu-premium {
  margin-top: 6px;
}

.premium-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: white;
}

.premium-until {
  font-size: 10px;
  color: var(--text-soft);
  margin-left: 8px;
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text);
}

.menu-item:hover {
  background: rgba(59,130,246,0.08);
}

.menu-item.danger:hover {
  background: rgba(239, 68, 68, 0.08);
  color: #ef4444;
}

.menu-divider {
  height: 1px;
  background: var(--border);
  margin: 8px 0;
}

.menu-row-icon {
  display: inline-flex;
  width: 20px;
  justify-content: center;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.modal-container {
  background: var(--bg);
  border-radius: 18px;
  border: 1px solid var(--border);
  width: 90%;
  max-width: 400px;
  overflow: hidden;
  animation: modalSlideIn 0.2s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid var(--border);
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
}

.modal-body {
  padding: 20px;
  color: var(--text-soft);
}

.modal-footer {
  padding: 12px 20px;
  border-top: 1px solid var(--border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.btn-cancel,
.btn-confirm {
  padding: 8px 16px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
}

.btn-cancel {
  background: transparent;
  border: 1px solid var(--border);
  color: var(--text);
}

.btn-cancel:hover {
  background: rgba(148, 163, 184, 0.1);
}

.btn-confirm {
  background: #ef4444;
  color: white;
}

.btn-confirm:hover {
  background: #dc2626;
}

@media (max-width: 1100px) {
  .header-inner {
    grid-template-columns: auto auto;
  }

  .header-center {
    display: none;
  }
}

@media (max-width: 768px) {
  .header-inner {
    height: 64px;
    padding: 0 14px;
    gap: 10px;
  }

  .brand-subtitle,
  .theme-text,
  .user-phone,
  .user-caret {
    display: none;
  }

  .brand-title {
    font-size: 16px;
  }

  .user-meta {
    display: none;
  }

  .theme-btn {
    padding: 0 10px;
  }

  .brand-logo {
    width: 38px;
    height: 38px;
    border-radius: 12px;
  }
}

@media (max-width: 520px) {
  .header-inner {
    padding: 0 12px;
  }

  .brand-text {
    max-width: 110px;
  }

  .brand-title {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .dropdown-menu {
    min-width: 260px;
    right: -10px;
  }
}
</style>